export interface NormalizedState<T, Key extends string | number = string> {
    allIds: Array<Key>
    byId: { [id: string]: T }
}

export const initNormalizedState = <
    T,
    Key extends string | number = string,
>(seedOpts?: {
    seedData: T[]
    getId: (data: T) => Key
    cloneObjects?: boolean
}): NormalizedState<T, Key> => ({
    allIds: seedOpts?.seedData.map(seedOpts?.getId) ?? [],
    byId:
        seedOpts?.seedData.reduce(
            (acc, data) => ({
                ...acc,
                [seedOpts.getId(data)]: seedOpts?.cloneObjects
                    ? { ...data }
                    : data,
            }),
            {},
        ) ?? {},
})

export const mergeNormalizedStates = <T, Key extends string | number = string>(
    ...toMerge: NormalizedState<T, Key>[]
): NormalizedState<T, Key> => {
    const merged: NormalizedState<T, Key> = initNormalizedState()

    for (const state of toMerge) {
        merged.allIds = [...new Set([...merged.allIds, ...state.allIds])]
        merged.byId = { ...merged.byId, ...state.byId }
    }

    return merged
}

export const cloneNormalizedState = <T, Key extends string | number = string>(
    state: NormalizedState<T, Key>,
): NormalizedState<T, Key> => ({
    allIds: [...state.allIds],
    byId: Object.entries(state.byId).reduce(
        (acc, [id, data]) => ({
            ...acc,
            [id]: { ...data },
        }),
        {},
    ),
})

export const normalizedStateToArray = <T, Key extends string | number = string>(
    state: NormalizedState<T, Key>,
): T[] => state.allIds.map((id) => state.byId[id])

export const mutateNormalizedStateValue = <
    T,
    Key extends string | number = string,
>(
    state: NormalizedState<T, Key>,
    key: Key,
    mutator: (data: T) => void,
): NormalizedState<T, Key> => {
    const newState = cloneNormalizedState(state)
    mutator(newState.byId[key])
    return newState
}
